import { ChangeEvent, useEffect, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { QuerySnapshot } from "firebase/firestore";

interface tuition{
    name:string,
    lesson:string,
    uid:string,
    tel:string,
    paied_date:string,
    unpaied:number,
    fee:string,
    method:string,
    etc:string,
    approver:string
}

interface student{
    name:string,
    tel:string,
    uid:string,
    fee:string,
}

interface tuitionInput{
    paied:string,
    method:string,
    etc:string
}

interface date{
    year:number,
    month:number
}

interface SortConfig {
    key: keyof tuition;
    direction: 'asc' | 'desc';
}

const useTuitionManage = () => {
    const today = new Date();
    const [tuitionList, setTuitionList] = useState<tuition[]>([]);
    const [searchedTuitionList, setSearchedTuitionList] = useState<tuition[]>([]);
    const { readFirestoreWithQuery, readFirestoreOneDoc, writeFirestore, updateFirestore } = useFirestore();
    const { getBranchId } = useGetBranchId();
    const [date, setDate] = useState<date>({
        year:today.getFullYear(),
        month:today.getMonth() + 1
    });
    const [payDate, setPayDate] = useState("1");
    const uid = sessionStorage.getItem("uid") || "";
    const [isOpenNullModal, setIsOpenNullModal] = useState(false);
    const [isOpenSucceedModal, setIsOpenSucceedModal] = useState(false);
    const [clickedStudent, setClickedStudent] = useState<string>("");
    const [isOpenPayDateSucceedModal, setIsOpenPayDateSucceedModal] = useState(false);
    const [showTuitionModal, setShowTuitionModal] = useState(false);
    const [nonpay, setNonpay] = useState(false);
    const [search, setSearch] = useState("");
    const [isOpenPayDateModal, setIsOpenPayDateModal] = useState(false);
    const [tuitionInput, setTuitionInput] = useState<tuitionInput>({
        paied:"0",
        method:"카드",
        etc:""
    })
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });

    useEffect(() => {
        getTuitionList();
        // eslint-disable-next-line
    }, [date])

    const onChange = (e:ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;

        setTuitionInput({
            ...tuitionInput,
            [name]:value
        })
    }  

    const getTuitionList = async() => {
        setTuitionList([]);
        setSearchedTuitionList([]);
        const branch_id = await getBranchId();
        const student_list = await getStudentList(branch_id);
        const pay_date = await getPayDate(branch_id);
        setPayDate(pay_date);

        const studentPromises = student_list.map(async (student) => {
            const lesson_name = await getLessonName(branch_id, student.uid);
            const tuition = await getTuition(branch_id, student.uid);
            if(!tuition.paied && pay_date === today.getDate()){
                await syncDatabaseIntegrity(student.uid);
            }
            const unpaied = await getUnPaied(student.uid);
            
            return {
                name: student.name,
                uid: student.uid,
                tel: student.tel,
                lesson: lesson_name,
                paied_date: tuition.paied_date,
                unpaied: unpaied,
                fee: student.fee,
                method: tuition.method,
                etc: tuition.etc,
                approver: tuition.approver
            };
        });

        const tuitionDataList = await Promise.all(studentPromises);
        setTuitionList(tuitionDataList);
        setSearchedTuitionList(tuitionDataList);
    }

    const syncDatabaseIntegrity = async(student_uid:string) => {
        const branch_id = await getBranchId();
        await writeFirestore(`Pay/Branch/${branch_id}/${date.year}/${date.month}`, student_uid, {
            paied:"0",
            method:"",
            etc:"",
            approver:"",
            paied_date:""
        })
        const student = await readFirestoreOneDoc("User/Auth/Student", student_uid).then(res => {
            if(res.exists()){
                return res.data();
            }
        })
        if(student){
            await updateFirestore("User/Auth/Student", student_uid, {unpaied:student.unpaied + student.fee});
        }
    }

    const getTuition = async(branch_id:string, student_uid:string) => {
        const tuition = await readFirestoreOneDoc(`Pay/Branch/${branch_id}/${date.year}/${date.month}`, student_uid).then(res => {
            if(res.exists()){
                return res.data();
            }
            return {paied_date:"", method:"", etc:"", approver:""};
        })
        return tuition;
    }

    const getStudentList = async(branch_id:string) => {
        const student_list = await readFirestoreWithQuery(`User/Auth/Student`, {field:"branch_id", operator:"==", data:branch_id}).then(res => {
            const list:student[] = [];
            const size = res.size;
            for(var i = 0; i < size; i++){
                const studentData = res.docs[i].data();
                if (studentData.status === 'active') {
                    list.push({
                        uid: res.docs[i].id, 
                        name: studentData.name, 
                        tel: studentData.parent_tel, 
                        fee: studentData.fee
                    });
                }
            }
            return list;
        })
        return student_list;
    }

    const getUnPaied = async(student_uid:string) => {
        const unpaied = await readFirestoreOneDoc("User/Auth/Student", student_uid).then(res => {
            if(res.exists()){
                return res.data().unpaied;
            }
        })
        return unpaied;
    }

    
    const getLessonName = async(branch_id:string, student_uid:string) => {
        const lesson_id = await readFirestoreWithQuery(`Assign/${branch_id}/Lesson`, {field:"students", operator:"array-contains", data:student_uid}).then((res:QuerySnapshot) => {
            if(!res.empty){
                return res.docs[0].id;
            }
            return "";
        })
        if(!lesson_id){
            return "";
        }
        
        const lesson_name = await readFirestoreOneDoc(`Lesson/Branch/${branch_id}`, lesson_id).then(res => {
            if(res.exists()){
                return res.data().name;
            }
            return "";
        })

        return lesson_name;
    }

    const getName = async() => {
        let name = "";

        name = await readFirestoreOneDoc("User/Auth/Branch", uid).then(res => {
            return res.exists() ? "원장" : name; 
        })

        name = await readFirestoreOneDoc("User/Auth/Teacher", uid).then(res => {
            return res.exists() ? res.data().name : name; 
        })
        return name;

    }

    const onPayClick = async() => {
        if(clickedStudent !== ""){
            if(tuitionInput.paied === "0"){
                setIsOpenNullModal(true);
            }
            const branch_id = await getBranchId();
            const name = await getName();
            const paied_date = `${today.getFullYear()}-${today.getMonth()+1}-${today.getDate()}`;
            await writeFirestore(`Pay/Branch/${branch_id}/${date.year}/${date.month}`, clickedStudent, {
                paied:tuitionInput.paied,
                method:tuitionInput.method,
                etc:tuitionInput.etc,
                approver:name,
                paied_date:paied_date,
            }).then(res => {
                if(res){
                    synchronize(name, paied_date);
                    setShowTuitionModal(false);
                }
            })
            const unpaied = await readFirestoreOneDoc("User/Auth/Student", clickedStudent).then(res => {
                if(res.exists()){
                    return res.data().unpaied
                }
            })
            await updateFirestore("User/Auth/Student", clickedStudent, {unpaied:unpaied - parseInt(tuitionInput.paied)}).then(res => {
                if(res){
                    setIsOpenSucceedModal(true);
                }
            })
        }
    }

    const getPayDate = async(branch_id:string) => {
        return await readFirestoreOneDoc("User/Auth/Branch", branch_id).then(res => {
            if(res.exists()){
                return res.data().pay_date;
            }
            return 1;
        })
    }

    const synchronize = async(approver:string, paied_date:string) => {
        let tuition_list = [...searchedTuitionList];
        const index = tuition_list.findIndex((item:tuition) => {
            return item.uid === clickedStudent;
        })
        tuition_list[index] = {...tuition_list[index],
            method:tuitionInput.method,
            etc:tuitionInput.etc,
            approver:approver,
            paied_date:paied_date,
            unpaied:tuition_list[index].unpaied - parseInt(tuitionInput.paied)
        }
        setSearchedTuitionList(tuition_list);
        setTuitionList(searchedTuitionList);
    }

    const onPayButtonClick = async(student_uid:string) => {
        const branch_id = await getBranchId();
        const tuition = await getTuition(branch_id, student_uid);
        const fee = await getFee(student_uid);
        setTuitionInput({
            paied:fee,
            method: "카드",
            etc:tuition.etc ? tuition.etc : ""
        })
        setClickedStudent(student_uid);
        setShowTuitionModal(true);
    }

    const onCancelClick = () => {
        setShowTuitionModal(false);
    }

    const onChangeDate = (e:ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setDate({
            ...date,
            [name]:value
        })
    }

    const getFee = async(student_uid:string) => {
        return await readFirestoreOneDoc("User/Auth/Student", student_uid).then(res => {
            if(res.exists()){
                return res.data().fee;
            }
            return "0"
        })

    }

    const onChangeNonpay = () => {
        if(nonpay){
            setNonpay(false)
        }else{
            setNonpay(true);
        }
    }

    const onChangeSearch = (e:ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }

    useEffect(() => {
        let sortedItems = tuitionList.filter((item) => item["name"].includes(search));
        
        if (sortConfig.key) {
            sortedItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        
        setSearchedTuitionList(sortedItems);
    }, [search, sortConfig, tuitionList]);


    const onPayDateClickHandler = async() => {
        setIsOpenPayDateModal(true);
    }

    const onChangePayDate = (e:ChangeEvent<HTMLSelectElement>) => {
        setPayDate(e.target.value);
    }

    const onPayDateModalCancelHandler = () => {
        setIsOpenPayDateModal(false);
    }

    const onPayDateModalConfirmClickHandler = async() => {
        const branch_id = await getBranchId();
        await updateFirestore("User/Auth/Branch", branch_id, {pay_date:parseInt(payDate)}).then(res => {
            if(res){
                setIsOpenPayDateSucceedModal(true)
            }
        });
    }

    const onPayDateSucceedModalConfirmClickHandler = () => {
        setIsOpenPayDateModal(false)
        setIsOpenPayDateSucceedModal(false);
    }

    const onSort = (key: keyof tuition) => {
        setSortConfig(current => {
            if (current.key === key) {
                return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    return {searchedTuitionList, onPayButtonClick, onChangeDate, showTuitionModal, onChange, tuitionInput, onPayClick, onCancelClick, date, onChangeNonpay, onChangeSearch, search, nonpay, 
        isOpenSucceedModal, setIsOpenSucceedModal, isOpenNullModal, setIsOpenNullModal, payDate, isOpenPayDateModal, onChangePayDate, onPayDateClickHandler, onPayDateModalCancelHandler, onPayDateModalConfirmClickHandler, isOpenPayDateSucceedModal, onPayDateSucceedModalConfirmClickHandler, sortConfig, onSort
    };
}

export default useTuitionManage;